import http from '@/http'

/**
 * Mixin for handling translatable content in forms
 *
 * Usage:
 * 1. Import and add to mixins: mixins: [translatableMixin]
 * 2. Call loadTranslationSettings() in mounted()
 * 3. Use translationData, activeLanguages, isMultiLanguage in your component
 * 4. Call saveTranslations(type, id) after saving the entity
 */
export default {
  data () {
    return {
      isMultiLanguage: false,
      activeLanguages: [],
      defaultLanguage: null,
      translationData: {}, // { languageId: { field: value, ... }, ... }
      translationLoading: false
    }
  },
  computed: {
    defaultLanguageId () {
      return this.defaultLanguage && this.defaultLanguage.id ? this.defaultLanguage.id : null
    }
  },
  methods: {
    /**
     * Load translation settings (active languages, multi-language status)
     */
    async loadTranslationSettings () {
      try {
        this.translationLoading = true
        const res = await http.get('/admin/content-translations/check-multi-language')

        // Handle different response structures
        const responseData = res.data.data || res.data

        if (responseData) {
          this.isMultiLanguage = responseData.is_multi_language || false
          this.activeLanguages = responseData.active_languages || responseData.languages || []
          this.defaultLanguage = responseData.default_language || null
        } else {
          console.warn('Unexpected API response structure:', res.data)
          this.isMultiLanguage = false
          this.activeLanguages = []
          this.defaultLanguage = null
        }
      } catch (error) {
        console.error('Error loading translation settings:', error)
        this.isMultiLanguage = false
        this.activeLanguages = []
        this.defaultLanguage = null
      } finally {
        this.translationLoading = false
      }
    },

    /**
     * Load existing translations for an entity
     * @param {string} type - Entity type (participant, room, setting)
     * @param {number} id - Entity ID
     */
    async loadTranslations (type, id) {
      if (!id) return

      try {
        this.translationLoading = true
        const res = await http.get('/admin/content-translations', {
          params: { type, id }
        })

        // Handle different response structures
        const responseData = res.data.data || res.data
        const translations = responseData.translations || {}
        this.translationData = {}

        // Convert from { langCode: { field: value } } to { langId: { field: value } }
        for (const lang of this.activeLanguages) {
          if (translations[lang.code]) {
            this.translationData[lang.id] = translations[lang.code]
          }
        }
      } catch (error) {
        console.error('Error loading translations:', error)
      } finally {
        this.translationLoading = false
      }
    },

    /**
     * Save translations for an entity
     * @param {string} type - Entity type (participant, room, setting)
     * @param {number} id - Entity ID
     */
    async saveTranslations (type, id) {
      if (!this.isMultiLanguage || !id) {
        return
      }

      try {
        // Filter out empty translations
        const translations = {}
        for (const [langId, fields] of Object.entries(this.translationData)) {
          const nonEmptyFields = {}
          for (const [field, value] of Object.entries(fields)) {
            if (value && value.trim() !== '') {
              nonEmptyFields[field] = value
            }
          }
          if (Object.keys(nonEmptyFields).length > 0) {
            translations[langId] = nonEmptyFields
          }
        }

        if (Object.keys(translations).length > 0) {
          await http.post('/admin/content-translations/sync', {
            type,
            id,
            translations
          })
        }
      } catch (error) {
        console.error('Error saving translations:', error)
        throw error
      }
    },

    /**
     * Clear translation data (useful when creating new entity)
     */
    clearTranslations () {
      this.translationData = {}
    },

    /**
     * Initialize translation data structure for all active languages
     * @param {Array} fields - Array of field names to initialize
     */
    initializeTranslations (fields = []) {
      const data = {}
      for (const lang of this.activeLanguages) {
        data[lang.id] = {}
        for (const field of fields) {
          data[lang.id][field] = ''
        }
      }
      // Set as a new object to ensure Vue reactivity
      this.translationData = data
    },

    /**
     * Set a translation value
     * @param {number} languageId
     * @param {string} field
     * @param {string} value
     */
    setTranslation (languageId, field, value) {
      if (!this.translationData[languageId]) {
        this.translationData[languageId] = {}
      }
      this.translationData[languageId][field] = value
    },

    /**
     * Get a translation value
     * @param {number} languageId
     * @param {string} field
     * @returns {string}
     */
    getTranslation (languageId, field) {
      if (this.translationData[languageId] && this.translationData[languageId][field]) {
        return this.translationData[languageId][field]
      }
      return ''
    }
  }
}
